import React, { useState, useEffect, useRef } from 'react';
import ImageUploadField from '../ImageUploadField';
import { hasPermission } from '../../utils/authHelper';
import api from '../../api/axiosInstance';
import AccessDenied from '../AccessDenied';
import { getImageUrl } from '../../utils/imageUrl';

const KM = {
  orange: 'var(--primary-600)',
  orangeLight: '#FEF0EB',
  blue: 'var(--primary-600)',
  green: '#39B54A',
  border: '#E5E7EB',
  text: '#1A1A2E',
  muted: '#6B7280',
  bg: '#F9FAFB',
};

export default function SiteSettings({ showToast }) {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // General Settings State
  const [logoUrl, setLogoUrl] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const logoInputRef = useRef(null);
  const [facebookUrl, setFacebookUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [twitterUrl, setTwitterUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [storePhone, setStorePhone] = useState('');
  const [storeEmail, setStoreEmail] = useState('');
  const [storeAddress, setStoreAddress] = useState('');

  // About Page State
  const [aboutImageUrl, setAboutImageUrl] = useState('');
  const [aboutImageFile, setAboutImageFile] = useState(null);
  const [aboutImagePreview, setAboutImagePreview] = useState('');
  const aboutImageInputRef = useRef(null);

  const [aboutTitle, setAboutTitle] = useState('');
  const [aboutSubtitle, setAboutSubtitle] = useState('');
  const [aboutDescPrimary, setAboutDescPrimary] = useState('');
  const [aboutDescSecondary, setAboutDescSecondary] = useState('');
  const [aboutFeature1, setAboutFeature1] = useState('');
  const [aboutFeature2, setAboutFeature2] = useState('');

  // Core Mission State
  const [missionTitle, setMissionTitle] = useState('');
  const [missionSubtitle, setMissionSubtitle] = useState('');
  const [missionDesc, setMissionDesc] = useState('');
  const [missionGridCol1Title, setMissionGridCol1Title] = useState('');
  const [missionGridCol1Desc, setMissionGridCol1Desc] = useState('');
  const [missionGridCol2Title, setMissionGridCol2Title] = useState('');
  const [missionGridCol2Desc, setMissionGridCol2Desc] = useState('');
  const [missionGridCol3Title, setMissionGridCol3Title] = useState('');
  const [missionGridCol3Desc, setMissionGridCol3Desc] = useState('');
  const [missionGridCol4Title, setMissionGridCol4Title] = useState('');
  const [missionGridCol4Desc, setMissionGridCol4Desc] = useState('');

  // CTA State
  const [ctaTitle, setCtaTitle] = useState('');
  const [ctaDesc, setCtaDesc] = useState('');
  const [ctaBtn1Text, setCtaBtn1Text] = useState('');
  const [ctaBtn2Text, setCtaBtn2Text] = useState('');

  // CTA & Intro Buttons
  const [aboutBtnText, setAboutBtnText] = useState('');

  // Statistics State
  const [stat1Count, setStat1Count] = useState('');
  const [stat1Label, setStat1Label] = useState('');
  const [stat2Count, setStat2Count] = useState('');
  const [stat2Label, setStat2Label] = useState('');
  const [stat3Count, setStat3Count] = useState('');
  const [stat3Label, setStat3Label] = useState('');
  const [stat4Count, setStat4Count] = useState('');
  const [stat4Label, setStat4Label] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/settings');
      const data = res.data || {};

      // General
      setLogoUrl(data.logoUrl || '');
      setLogoPreview(getImageUrl(data.logoUrl));
      setFacebookUrl(data.facebookUrl || '');
      setInstagramUrl(data.instagramUrl || '');
      setTwitterUrl(data.twitterUrl || '');
      setYoutubeUrl(data.youtubeUrl || '');
      setStorePhone(data.storePhone || data.phone || '+91 9449 818 329');
      setStoreEmail(data.storeEmail || data.email || 'healthyhomesales@gmail.com');
      setStoreAddress(data.storeAddress || data.address || 'A306 Durga Petals Apartment Near rainbow children hospital Doddanekundi Bangalore 560 037');

      // About
      setAboutImageUrl(data.aboutImageUrl || '');
      setAboutImagePreview(getImageUrl(data.aboutImageUrl));
      setAboutTitle(data.aboutTitle || 'Nourishing Lives with Pure, Natural Goodness');
      setAboutSubtitle(data.aboutSubtitle || 'Our Mission');
      setAboutDescPrimary(data.aboutDescPrimary || 'Welcome to Healthy Home, where tradition meets purity. We believe that true well-being starts from the roots—which is why we are dedicated to bringing you the highest quality unrefined, cold-pressed oils, traditional sweeteners, natural salts, and farm-fresh dairy.');
      setAboutDescSecondary(data.aboutDescSecondary || "Whether you're looking for our signature Cold Pressed Groundnut Oil or our pure Himalayan Rock Salt, every product we craft is made with care, sustainability, and absolute transparency. Our journey began with a simple idea: to make healthy, chemical-free living accessible to every family.");
      setAboutFeature1(data.aboutFeature1 || '100% Organic');
      setAboutFeature2(data.aboutFeature2 || 'Premium Quality');
      setAboutBtnText(data.aboutBtnText || 'Shop Now');

      // Core Mission
      setMissionTitle(data.missionTitle || 'Our Core Values');
      setMissionSubtitle(data.missionSubtitle || 'What We Stand For');
      setMissionDesc(data.missionDesc || 'Everything we do is guided by a simple set of principles that keep us honest to our roots.');
      setMissionGridCol1Title(data.missionGridCol1Title || 'Sourced Responsibly');
      setMissionGridCol1Desc(data.missionGridCol1Desc || 'We work directly with small farms and trusted producers, cutting out middlemen so quality and fairness stay intact.');
      setMissionGridCol2Title(data.missionGridCol2Title || 'No Additives, Ever');
      setMissionGridCol2Desc(data.missionGridCol2Desc || 'Every product is free from preservatives, refined chemicals, and artificial shortcuts — just what nature intended.');
      setMissionGridCol3Title(data.missionGridCol3Title || 'Sustainable Practices');
      setMissionGridCol3Desc(data.missionGridCol3Desc || 'From cold-pressing methods to eco-friendly packaging, we minimize our footprint at every step of the process.');
      setMissionGridCol4Title(data.missionGridCol4Title || 'Community First');
      setMissionGridCol4Desc(data.missionGridCol4Desc || 'A share of every purchase supports the farming communities and artisans who make our products possible.');

      // CTA
      setCtaTitle(data.ctaTitle || 'Ready to Taste the Difference?');
      setCtaDesc(data.ctaDesc || 'Explore our full range of cold-pressed oils, natural salts, and traditional sweeteners, crafted for a healthier home.');
      setCtaBtn1Text(data.ctaBtn1Text || 'Shop Now');
      setCtaBtn2Text(data.ctaBtn2Text || 'Get in Touch');

      // Statistics
      setStat1Count(data.stat1Count || '50+');
      setStat1Label(data.stat1Label || 'Natural Products');
      setStat2Count(data.stat2Count || '25,000+');
      setStat2Label(data.stat2Label || 'Happy Families');
      setStat3Count(data.stat3Count || '120+');
      setStat3Label(data.stat3Label || 'Partner Farms');
      setStat4Count(data.stat4Count || '8');
      setStat4Label(data.stat4Label || 'Years of Trust');

    } catch (err) {
      showToast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    const toastId = showToast.loading('Reloading settings...');
    try {
      const res = await api.get('/settings');
      const data = res.data || {};

      // General
      setLogoUrl(data.logoUrl || '');
      setLogoPreview(getImageUrl(data.logoUrl));
      setLogoFile(null);
      if (logoInputRef.current) logoInputRef.current.value = '';
      setFacebookUrl(data.facebookUrl || '');
      setInstagramUrl(data.instagramUrl || '');
      setTwitterUrl(data.twitterUrl || '');
      setYoutubeUrl(data.youtubeUrl || '');
      setStorePhone(data.storePhone || data.phone || '+91 9449 818 329');
      setStoreEmail(data.storeEmail || data.email || 'healthyhomesales@gmail.com');
      setStoreAddress(data.storeAddress || data.address || 'A306 Durga Petals Apartment Near rainbow children hospital Doddanekundi Bangalore 560 037');

      // About
      setAboutImageUrl(data.aboutImageUrl || '');
      setAboutImagePreview(getImageUrl(data.aboutImageUrl));
      setAboutImageFile(null);
      if (aboutImageInputRef.current) aboutImageInputRef.current.value = '';

      setAboutTitle(data.aboutTitle || 'Nourishing Lives with Pure, Natural Goodness');
      setAboutSubtitle(data.aboutSubtitle || 'Our Mission');
      setAboutDescPrimary(data.aboutDescPrimary || 'Welcome to Healthy Home, where tradition meets purity. We believe that true well-being starts from the roots—which is why we are dedicated to bringing you the highest quality unrefined, cold-pressed oils, traditional sweeteners, natural salts, and farm-fresh dairy.');
      setAboutDescSecondary(data.aboutDescSecondary || "Whether you're looking for our signature Cold Pressed Groundnut Oil or our pure Himalayan Rock Salt, every product we craft is made with care, sustainability, and absolute transparency. Our journey began with a simple idea: to make healthy, chemical-free living accessible to every family.");
      setAboutFeature1(data.aboutFeature1 || '100% Organic');
      setAboutFeature2(data.aboutFeature2 || 'Premium Quality');
      setAboutBtnText(data.aboutBtnText || 'Shop Now');

      // Core Mission
      setMissionTitle(data.missionTitle || 'Our Core Values');
      setMissionSubtitle(data.missionSubtitle || 'What We Stand For');
      setMissionDesc(data.missionDesc || 'Everything we do is guided by a simple set of principles that keep us honest to our roots.');
      setMissionGridCol1Title(data.missionGridCol1Title || 'Sourced Responsibly');
      setMissionGridCol1Desc(data.missionGridCol1Desc || 'We work directly with small farms and trusted producers, cutting out middlemen so quality and fairness stay intact.');
      setMissionGridCol2Title(data.missionGridCol2Title || 'No Additives, Ever');
      setMissionGridCol2Desc(data.missionGridCol2Desc || 'Every product is free from preservatives, refined chemicals, and artificial shortcuts — just what nature intended.');
      setMissionGridCol3Title(data.missionGridCol3Title || 'Sustainable Practices');
      setMissionGridCol3Desc(data.missionGridCol3Desc || 'From cold-pressing methods to eco-friendly packaging, we minimize our footprint at every step of the process.');
      setMissionGridCol4Title(data.missionGridCol4Title || 'Community First');
      setMissionGridCol4Desc(data.missionGridCol4Desc || 'A share of every purchase supports the farming communities and artisans who make our products possible.');

      // CTA
      setCtaTitle(data.ctaTitle || 'Ready to Taste the Difference?');
      setCtaDesc(data.ctaDesc || 'Explore our full range of cold-pressed oils, natural salts, and traditional sweeteners, crafted for a healthier home.');
      setCtaBtn1Text(data.ctaBtn1Text || 'Shop Now');
      setCtaBtn2Text(data.ctaBtn2Text || 'Get in Touch');

      // Statistics
      setStat1Count(data.stat1Count || '50+');
      setStat1Label(data.stat1Label || 'Natural Products');
      setStat2Count(data.stat2Count || '25,000+');
      setStat2Label(data.stat2Label || 'Happy Families');
      setStat3Count(data.stat3Count || '120+');
      setStat3Label(data.stat3Label || 'Partner Farms');
      setStat4Count(data.stat4Count || '8');
      setStat4Label(data.stat4Label || 'Years of Trust');

      showToast.success('Settings reloaded to saved state', toastId);
    } catch (err) {
      showToast.error('Failed to load settings', toastId);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleAboutImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAboutImageFile(file);
      setAboutImagePreview(URL.createObjectURL(file));
    }
  };

  const handleClearLogo = () => {
    setLogoFile(null);
    setLogoUrl('');
    setLogoPreview('');
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleClearAboutImage = () => {
    setAboutImageFile(null);
    setAboutImageUrl('');
    setAboutImagePreview('');
    if (aboutImageInputRef.current) aboutImageInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission('settings_edit')) {
      showToast.error('You do not have permission to edit settings');
      return;
    }

    setSaving(true);
    const toastId = showToast.loading('Saving settings...');

    try {
      const formData = new FormData();

      // Append text settings
      formData.append('facebookUrl', facebookUrl || '');
      formData.append('instagramUrl', instagramUrl || '');
      formData.append('twitterUrl', twitterUrl || '');
      formData.append('youtubeUrl', youtubeUrl || '');
      formData.append('storePhone', storePhone || '');
      formData.append('storeEmail', storeEmail || '');
      formData.append('storeAddress', storeAddress || '');

      formData.append('aboutTitle', aboutTitle);
      formData.append('aboutSubtitle', aboutSubtitle);
      formData.append('aboutDescPrimary', aboutDescPrimary);
      formData.append('aboutDescSecondary', aboutDescSecondary);
      formData.append('aboutFeature1', aboutFeature1);
      formData.append('aboutFeature2', aboutFeature2);
      formData.append('aboutBtnText', aboutBtnText);

      formData.append('missionTitle', missionTitle);
      formData.append('missionSubtitle', missionSubtitle);
      formData.append('missionDesc', missionDesc);
      formData.append('missionGridCol1Title', missionGridCol1Title);
      formData.append('missionGridCol1Desc', missionGridCol1Desc);
      formData.append('missionGridCol2Title', missionGridCol2Title);
      formData.append('missionGridCol2Desc', missionGridCol2Desc);
      formData.append('missionGridCol3Title', missionGridCol3Title);
      formData.append('missionGridCol3Desc', missionGridCol3Desc);
      formData.append('missionGridCol4Title', missionGridCol4Title);
      formData.append('missionGridCol4Desc', missionGridCol4Desc);

      formData.append('ctaTitle', ctaTitle);
      formData.append('ctaDesc', ctaDesc);
      formData.append('ctaBtn1Text', ctaBtn1Text);
      formData.append('ctaBtn2Text', ctaBtn2Text);

      formData.append('stat1Count', stat1Count);
      formData.append('stat1Label', stat1Label);
      formData.append('stat2Count', stat2Count);
      formData.append('stat2Label', stat2Label);
      formData.append('stat3Count', stat3Count);
      formData.append('stat3Label', stat3Label);
      formData.append('stat4Count', stat4Count);
      formData.append('stat4Label', stat4Label);

      // Append cleared paths if cleared
      if (!logoUrl && !logoFile) {
        formData.append('logoUrl', '');
      }
      if (!aboutImageUrl && !aboutImageFile) {
        formData.append('aboutImageUrl', '');
      }

      // Append files if selected
      if (logoFile) {
        formData.append('logo', logoFile);
      }
      if (aboutImageFile) {
        formData.append('aboutImage', aboutImageFile);
      }

      const res = await api.put('/settings', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const updated = res.data.settings || {};

      // Update local values with saved ones
      setLogoUrl(updated.logoUrl || '');
      setLogoPreview(getImageUrl(updated.logoUrl));
      setAboutImageUrl(updated.aboutImageUrl || '');
      setAboutImagePreview(getImageUrl(updated.aboutImageUrl));

      setLogoFile(null);
      setAboutImageFile(null);

      showToast.success('Settings updated successfully', toastId);

      // Dispatch custom event to notify Sidebar / Topbar about logo change
      window.dispatchEvent(new Event('site-settings-updated'));

    } catch (err) {
      showToast.error(err.response?.data?.message || 'Failed to save settings', toastId);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    padding: '9px 12px',
    border: `1px solid ${KM.border}`,
    borderRadius: 8,
    fontSize: 13,
    color: KM.text,
    background: '#fff',
    fontFamily: 'inherit',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    fontSize: 11,
    fontWeight: 500,
    color: KM.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  if (!hasPermission('settings_view')) {
    return <AccessDenied moduleName="Site Settings" />;
  }

  if (loading) {
    return (
      <div className="categories-container">
        <div className="section-header">
          <div className="section-title">Site Settings</div>
        </div>
        <p className="km-loading">Loading site settings...</p>
      </div>
    );
  }

  return (
    <div className="categories-container" style={{ paddingBottom: 40 }}>
      <div className="section-header">
        <div className="section-title">Site Settings</div>
      </div>

      {/* Tabs */}
      <div className="tabs-container" style={{ display: 'flex', gap: 15, borderBottom: `1px solid ${KM.border}`, marginBottom: 25, paddingBottom: 5 }}>
        <button
          type="button"
          onClick={() => setActiveTab('general')}
          style={{
            padding: '10px 15px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'general' ? `2px solid ${KM.orange}` : 'none',
            color: activeTab === 'general' ? KM.orange : KM.muted,
            fontWeight: activeTab === 'general' ? 600 : 500,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          General & Logo
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('about')}
          style={{
            padding: '10px 15px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'about' ? `2px solid ${KM.orange}` : 'none',
            color: activeTab === 'about' ? KM.orange : KM.muted,
            fontWeight: activeTab === 'about' ? 600 : 500,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          About "Who We Are"
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('mission')}
          style={{
            padding: '10px 15px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'mission' ? `2px solid ${KM.orange}` : 'none',
            color: activeTab === 'mission' ? KM.orange : KM.muted,
            fontWeight: activeTab === 'mission' ? 600 : 500,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Core Mission Pillars
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('stats')}
          style={{
            padding: '10px 15px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'stats' ? `2px solid ${KM.orange}` : 'none',
            color: activeTab === 'stats' ? KM.orange : KM.muted,
            fontWeight: activeTab === 'stats' ? 600 : 500,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Statistics Counters
        </button>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* TAB 1: GENERAL & LOGO */}
        {activeTab === 'general' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* <div className="km-form-card fade-in" style={{ background: '#fff', border: `1px solid ${KM.border}`, borderRadius: 12, padding: 24 }}>
              <h3 style={{ fontSize: 16, margin: '0 0 16px 0', fontWeight: 600, color: KM.text }}>Storefront & Admin Logo</h3>
              <div style={{ maxWidth: 500 }}>
                <ImageUploadField
                  label="Store Logo"
                  imageFile={logoFile}
                  preview={logoPreview}
                  fileInputRef={logoInputRef}
                  onFileChange={handleLogoChange}
                  onClear={handleClearLogo}
                  accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,image/bmp,image/tiff,image/x-icon,image/heic,image/heif,image/avif"
                  requirements="Horizontal orientation recommended • Max: 3MB (Common Image Formats)"
                />
              </div>
            </div> */}

            <div className="km-form-card fade-in" style={{ background: '#fff', border: `1px solid ${KM.border}`, borderRadius: 12, padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ fontSize: 16, margin: 0, fontWeight: 600, color: KM.text }}>Social Media Profiles</h3>
                <span style={{ fontSize: 12, color: KM.muted }}>Only platforms with entered URLs will appear in the website footer</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <label style={labelStyle}>Facebook Page URL</label>
                  <input
                    value={facebookUrl}
                    onChange={e => setFacebookUrl(e.target.value)}
                    style={inputStyle}
                    placeholder="https://www.facebook.com/yourpage"
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <label style={labelStyle}>Instagram Profile URL</label>
                  <input
                    value={instagramUrl}
                    onChange={e => setInstagramUrl(e.target.value)}
                    style={inputStyle}
                    placeholder="https://www.instagram.com/yourprofile"
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <label style={labelStyle}>Twitter / X URL</label>
                  <input
                    value={twitterUrl}
                    onChange={e => setTwitterUrl(e.target.value)}
                    style={inputStyle}
                    placeholder="https://www.twitter.com/yourhandle"
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <label style={labelStyle}>YouTube Channel URL</label>
                  <input
                    value={youtubeUrl}
                    onChange={e => setYoutubeUrl(e.target.value)}
                    style={inputStyle}
                    placeholder="https://www.youtube.com/@yourchannel"
                  />
                </div>
              </div>
            </div>

            {/* <div className="km-form-card fade-in" style={{ background: '#fff', border: `1px solid ${KM.border}`, borderRadius: 12, padding: 24 }}>
              <h3 style={{ fontSize: 16, margin: '0 0 16px 0', fontWeight: 600, color: KM.text }}>Store Contact Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <label style={labelStyle}>Phone Number</label>
                  <input
                    value={storePhone}
                    onChange={e => setStorePhone(e.target.value)}
                    style={inputStyle}
                    placeholder="+91 9449 818 329"
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <label style={labelStyle}>Support Email</label>
                  <input
                    value={storeEmail}
                    onChange={e => setStoreEmail(e.target.value)}
                    style={inputStyle}
                    placeholder="healthyhomesales@gmail.com"
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, gridColumn: 'span 2' }}>
                  <label style={labelStyle}>Store Address</label>
                  <input
                    value={storeAddress}
                    onChange={e => setStoreAddress(e.target.value)}
                    style={inputStyle}
                    placeholder="A306 Durga Petals Apartment Near rainbow children hospital Doddanekundi Bangalore 560 037"
                  />
                </div>
              </div>
            </div> */}
          </div>
        )}

        {/* TAB 2: ABOUT US PAGE */}
        {activeTab === 'about' && (
          <div className="km-form-card fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 20, background: '#fff', border: `1px solid ${KM.border}`, borderRadius: 12, padding: 24 }}>
            <h3 style={{ fontSize: 16, margin: 0, fontWeight: 600, color: KM.text }}>About Page Header Section</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <label style={labelStyle}>Section Subtitle</label>
                  <input
                    value={aboutSubtitle}
                    onChange={e => setAboutSubtitle(e.target.value)}
                    style={inputStyle}
                    placeholder="e.g. Our Mission"
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <label style={labelStyle}>Welcome Main Title</label>
                  <input
                    value={aboutTitle}
                    onChange={e => setAboutTitle(e.target.value)}
                    style={inputStyle}
                    placeholder="e.g. Nourishing Lives with Pure, Natural Goodness"
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <label style={labelStyle}>Feature 1</label>
                  <input
                    value={aboutFeature1}
                    onChange={e => setAboutFeature1(e.target.value)}
                    style={inputStyle}
                    placeholder="e.g. 100% Organic"
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <label style={labelStyle}>Feature 2</label>
                  <input
                    value={aboutFeature2}
                    onChange={e => setAboutFeature2(e.target.value)}
                    style={inputStyle}
                    placeholder="e.g. Premium Quality"
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <label style={labelStyle}>Intro Button Text</label>
                  <input
                    value={aboutBtnText}
                    onChange={e => setAboutBtnText(e.target.value)}
                    style={inputStyle}
                    placeholder="e.g. Shop Now"
                  />
                </div>
              </div>

              <div>
                <ImageUploadField
                  label="Welcome / About Image"
                  imageFile={aboutImageFile}
                  preview={aboutImagePreview}
                  fileInputRef={aboutImageInputRef}
                  onFileChange={handleAboutImageChange}
                  onClear={handleClearAboutImage}
                  accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,image/bmp,image/tiff,image/x-icon,image/heic,image/heif,image/avif"
                  requirements="570×370px recommended • Max: 3MB (Common Image Formats)"
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={labelStyle}>Description Paragraph 1</label>
              <textarea
                value={aboutDescPrimary}
                onChange={e => setAboutDescPrimary(e.target.value)}
                style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                placeholder="First paragraph..."
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={labelStyle}>Description Paragraph 2</label>
              <textarea
                value={aboutDescSecondary}
                onChange={e => setAboutDescSecondary(e.target.value)}
                style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                placeholder="Second paragraph..."
              />
            </div>
          </div>
        )}

        {/* TAB 3: CORE MISSION PILLARS */}
        {activeTab === 'mission' && (
          <div className="km-form-card fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 20, background: '#fff', border: `1px solid ${KM.border}`, borderRadius: 12, padding: 24 }}>
            <h3 style={{ fontSize: 16, margin: 0, fontWeight: 600, color: KM.text }}>Core Mission & Pillars (About Page)</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={labelStyle}>Section Header Title</label>
                <input
                  value={missionTitle}
                  onChange={e => setMissionTitle(e.target.value)}
                  style={inputStyle}
                  placeholder="e.g. Our Core Mission"
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={labelStyle}>Section Header Subtitle</label>
                <input
                  value={missionSubtitle}
                  onChange={e => setMissionSubtitle(e.target.value)}
                  style={inputStyle}
                  placeholder="e.g. What We Stand For"
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, gridColumn: 'span 2' }}>
                <label style={labelStyle}>Section Description</label>
                <textarea
                  value={missionDesc}
                  onChange={e => setMissionDesc(e.target.value)}
                  style={{ ...inputStyle, minHeight: 60 }}
                  placeholder="Description..."
                />
              </div>
            </div>

            <hr style={{ border: 0, borderTop: `1px solid ${KM.border}`, margin: '10px 0' }} />

            {/* Grid 1 */}
            <div style={{ background: '#fcfcfc', border: `1px solid ${KM.border}`, borderRadius: 8, padding: 15 }}>
              <h4 style={{ fontSize: 13, margin: '0 0 10px 0', fontWeight: 600, color: KM.orange }}>Pillar 1 (e.g. Traditional Wooden Ghani)</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input
                  value={missionGridCol1Title}
                  onChange={e => setMissionGridCol1Title(e.target.value)}
                  style={inputStyle}
                  placeholder="e.g. 100% Traditional Wooden Ghani"
                />
                <textarea
                  value={missionGridCol1Desc}
                  onChange={e => setMissionGridCol1Desc(e.target.value)}
                  style={{ ...inputStyle, minHeight: 60 }}
                  placeholder="e.g. We extract unrefined oils at low temperatures without chemicals or artificial refining."
                />
              </div>
            </div>

            {/* Grid 2 */}
            <div style={{ background: '#fcfcfc', border: `1px solid ${KM.border}`, borderRadius: 8, padding: 15 }}>
              <h4 style={{ fontSize: 13, margin: '0 0 10px 0', fontWeight: 600, color: KM.orange }}>Pillar 2 (e.g. Direct Farm Sourcing)</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input
                  value={missionGridCol2Title}
                  onChange={e => setMissionGridCol2Title(e.target.value)}
                  style={inputStyle}
                  placeholder="e.g. Direct Farm Sourcing"
                />
                <textarea
                  value={missionGridCol2Desc}
                  onChange={e => setMissionGridCol2Desc(e.target.value)}
                  style={{ ...inputStyle, minHeight: 60 }}
                  placeholder="e.g. Sourced directly from trusted organic farmers, traditional ghani mills, and forest beekeepers."
                />
              </div>
            </div>

            {/* Grid 3 */}
            <div style={{ background: '#fcfcfc', border: `1px solid ${KM.border}`, borderRadius: 8, padding: 15 }}>
              <h4 style={{ fontSize: 13, margin: '0 0 10px 0', fontWeight: 600, color: KM.orange }}>Pillar 3 (e.g. Chemical &amp; Preservative Free)</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input
                  value={missionGridCol3Title}
                  onChange={e => setMissionGridCol3Title(e.target.value)}
                  style={inputStyle}
                  placeholder="e.g. Chemical &amp; Preservative Free"
                />
                <textarea
                  value={missionGridCol3Desc}
                  onChange={e => setMissionGridCol3Desc(e.target.value)}
                  style={{ ...inputStyle, minHeight: 60 }}
                  placeholder="e.g. Zero adulteration, mineral oil, artificial colors, synthetic flavors, or harmful preservatives."
                />
              </div>
            </div>

            {/* Grid 4 */}
            <div style={{ background: '#fcfcfc', border: `1px solid ${KM.border}`, borderRadius: 8, padding: 15 }}>
              <h4 style={{ fontSize: 13, margin: '0 0 10px 0', fontWeight: 600, color: KM.orange }}>Pillar 4 (e.g. Holistic Health &amp; Wellness)</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input
                  value={missionGridCol4Title}
                  onChange={e => setMissionGridCol4Title(e.target.value)}
                  style={inputStyle}
                  placeholder="e.g. Holistic Health &amp; Wellness"
                />
                <textarea
                  value={missionGridCol4Desc}
                  onChange={e => setMissionGridCol4Desc(e.target.value)}
                  style={{ ...inputStyle, minHeight: 60 }}
                  placeholder="e.g. Reviving traditional dietary wisdom and nutritious culinary habits for every household."
                />
              </div>
            </div>

            <hr style={{ border: 0, borderTop: `1px solid ${KM.border}`, margin: '10px 0' }} />
            <h3 style={{ fontSize: 16, margin: 0, fontWeight: 600, color: KM.text }}>Call To Action (CTA) Banner</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 15 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={labelStyle}>CTA Title</label>
                <input
                  value={ctaTitle}
                  onChange={e => setCtaTitle(e.target.value)}
                  style={inputStyle}
                  placeholder="e.g. Ready to Taste the Difference?"
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={labelStyle}>CTA Description</label>
                <textarea
                  value={ctaDesc}
                  onChange={e => setCtaDesc(e.target.value)}
                  style={{ ...inputStyle, minHeight: 60 }}
                  placeholder="Description..."
                />
              </div>
              <div style={{ display: 'flex', gap: 15 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
                  <label style={labelStyle}>Button 1 Text</label>
                  <input
                    value={ctaBtn1Text}
                    onChange={e => setCtaBtn1Text(e.target.value)}
                    style={inputStyle}
                    placeholder="e.g. Shop Now"
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
                  <label style={labelStyle}>Button 2 Text</label>
                  <input
                    value={ctaBtn2Text}
                    onChange={e => setCtaBtn2Text(e.target.value)}
                    style={inputStyle}
                    placeholder="e.g. Get in Touch"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: STATISTICS COUNTERS */}
        {activeTab === 'stats' && (
          <div className="km-form-card fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 20, background: '#fff', border: `1px solid ${KM.border}`, borderRadius: 12, padding: 24 }}>
            <h3 style={{ fontSize: 16, margin: 0, fontWeight: 600, color: KM.text }}>About Page Statistics Counters</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* Stat 1 */}
              <div style={{ background: '#fcfcfc', border: `1px solid ${KM.border}`, borderRadius: 8, padding: 15 }}>
                <h4 style={{ fontSize: 13, margin: '0 0 10px 0', fontWeight: 600, color: KM.orange }}>Counter Item 1</h4>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Number</label>
                    <input value={stat1Count} onChange={e => setStat1Count(e.target.value)} style={inputStyle} placeholder="e.g. 1200" />
                  </div>
                  <div style={{ flex: 2 }}>
                    <label style={labelStyle}>Label</label>
                    <input value={stat1Label} onChange={e => setStat1Label(e.target.value)} style={inputStyle} placeholder="e.g. Natural Products" />
                  </div>
                </div>
              </div>

              {/* Stat 2 */}
              <div style={{ background: '#fcfcfc', border: `1px solid ${KM.border}`, borderRadius: 8, padding: 15 }}>
                <h4 style={{ fontSize: 13, margin: '0 0 10px 0', fontWeight: 600, color: KM.orange }}>Counter Item 2</h4>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Number</label>
                    <input value={stat2Count} onChange={e => setStat2Count(e.target.value)} style={inputStyle} placeholder="e.g. 850" />
                  </div>
                  <div style={{ flex: 2 }}>
                    <label style={labelStyle}>Label</label>
                    <input value={stat2Label} onChange={e => setStat2Label(e.target.value)} style={inputStyle} placeholder="e.g. Happy Families" />
                  </div>
                </div>
              </div>

              {/* Stat 3 */}
              <div style={{ background: '#fcfcfc', border: `1px solid ${KM.border}`, borderRadius: 8, padding: 15 }}>
                <h4 style={{ fontSize: 13, margin: '0 0 10px 0', fontWeight: 600, color: KM.orange }}>Counter Item 3</h4>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Number</label>
                    <input value={stat3Count} onChange={e => setStat3Count(e.target.value)} style={inputStyle} placeholder="e.g. 25" />
                  </div>
                  <div style={{ flex: 2 }}>
                    <label style={labelStyle}>Label</label>
                    <input value={stat3Label} onChange={e => setStat3Label(e.target.value)} style={inputStyle} placeholder="e.g. Partner Farms" />
                  </div>
                </div>
              </div>

              {/* Stat 4 */}
              <div style={{ background: '#fcfcfc', border: `1px solid ${KM.border}`, borderRadius: 8, padding: 15 }}>
                <h4 style={{ fontSize: 13, margin: '0 0 10px 0', fontWeight: 600, color: KM.orange }}>Counter Item 4</h4>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Number</label>
                    <input value={stat4Count} onChange={e => setStat4Count(e.target.value)} style={inputStyle} placeholder="e.g. 500" />
                  </div>
                  <div style={{ flex: 2 }}>
                    <label style={labelStyle}>Label</label>
                    <input value={stat4Label} onChange={e => setStat4Label(e.target.value)} style={inputStyle} placeholder="e.g. Years of Trust" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form Action Buttons */}
        {hasPermission('settings_edit') && (
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '11px 22px',
                background: KM.blue,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={saving}
              style={{
                padding: '11px 22px',
                background: '#fff',
                border: `1px solid ${KM.border}`,
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                color: KM.muted,
                cursor: 'pointer',
              }}
            >
              Reset / Reload
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
